import { handleTokenExpired } from "@/utils/errorHandler";
import { request } from "@umijs/max";
import type { ApiResponse } from "types";

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{
    message: string;
    extensions?: Record<string, any>;
  }>;
}

/**
 * GraphQL 请求客户端
 * 自动转换为统一的 ApiResponse 格式
 */
class GraphQLClient {
  private endpoint: string;

  constructor(endpoint: string = "/graphql") {
    this.endpoint = endpoint;
  }

  /**
   * 执行 GraphQL 查询或变更
   * @param query GraphQL 查询字符串
   * @param variables 查询变量
   * @param endpoint 请求接口地址
   * @returns 统一格式的响应
   */
  async request<T = any>(
    query: string,
    variables?: Record<string, any>,
    endpoint?: string
  ): Promise<ApiResponse<T>> {
    try {
      const response = await request<GraphQLResponse<T>>(
        endpoint ?? this.endpoint,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          data: {
            query,
            variables: variables || {},
          },
        }
      );
      // 处理 GraphQL 错误
      if (response.errors && response.errors.length > 0) {
        const error = response.errors[0];
        const errorMessage = error.message || 'GraphQL Error';
        const errorCode = error?.extensions?.code;
        const errorStatus = error?.extensions?.status;

        console.warn('[GraphQL Error]', {
          errorCode,
          errorStatus,
          errorMessage,
        });

        // 🔑 检查是否是登陆超时错误
        if (
          errorCode === 'UNAUTHORIZED' ||
          errorCode === 'UNAUTHENTICATED' ||
          errorStatus === 401 ||
          errorStatus === 403
        ) {
          console.error('[Auth Error Detected] Calling handleTokenExpired');
          handleTokenExpired();
          
          // 返回拒绝的 Promise，让调用方知道出错了
          return Promise.reject(new Error('Token expired'));
        }

        // 其他 GraphQL 错误
        return {
          success: false,
          errorCode: errorCode || 'GRAPHQL_ERROR',
          errorMessage,
          showType: 2,
        };
      }

      // ============ 成功响应 ============
      return {
        success: true,
        data: response.data,
      };
    } catch (error:any) {      
      const errorMessage = error.message ?? '请求失败';
      return {
        success: false,
        errorCode: 'NETWORK_ERROR',
        errorMessage,
        showType: 2,
      };
    }
  }
}

export const graphqlClient = new GraphQLClient();
